'use client';

import React, { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface CalculatorInputs {
  teamSize: number;
  projectDuration: number;
  currentEfficiency: number;
}

interface ROICalculatorProps {
  title: string;
  description: string;
}

const ROICalculator = ({ title, description }: ROICalculatorProps) => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [inputs, setInputs] = useState<CalculatorInputs>({
    teamSize: 10,
    projectDuration: 6,
    currentEfficiency: 70
  });

  const [results, setResults] = useState({
    costSavings: 0,
    timeReduction: 0,
    productivityGain: 0,
    roi: 0
  });

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  useEffect(() => {
    if (isHydrated) {
      calculateROI();
    }
  }, [inputs, isHydrated]);

  const calculateROI = () => {
    const avgSalary = 120000;
    const monthlyCost = (avgSalary / 12) * inputs.teamSize;
    const projectCost = monthlyCost * inputs.projectDuration;
    
    const efficiencyImprovement = (100 - inputs.currentEfficiency) * 0.4;
    const costSavings = projectCost * (efficiencyImprovement / 100);
    const timeReduction = inputs.projectDuration * (efficiencyImprovement / 100);
    const productivityGain = efficiencyImprovement;
    const roi = (costSavings / (projectCost * 0.15)) * 100;

    setResults({
      costSavings: Math.round(costSavings),
      timeReduction: Math.round(timeReduction * 10) / 10,
      productivityGain: Math.round(productivityGain),
      roi: Math.round(roi)
    });
  };

  const handleInputChange = (field: keyof CalculatorInputs, value: number) => {
    setInputs(prev => ({
      ...prev,
      [field]: value
    }));
  };

  if (!isHydrated) {
    return (
      <section className="py-16 bg-gradient-to-br from-primary to-secondary">
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-headline font-bold text-primary-foreground mb-4">
              {title}
            </h2>
            <p className="text-lg text-primary-foreground opacity-90 max-w-3xl mx-auto">
              {description}
            </p>
          </div>
          <div className="bg-card rounded-lg p-8 shadow-partnership">
            <div className="animate-pulse space-y-6">
              <div className="h-20 bg-muted rounded" />
              <div className="h-20 bg-muted rounded" />
              <div className="h-20 bg-muted rounded" />
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-16 bg-gradient-to-br from-primary to-secondary">
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
        <div className="text-center mb-12">
          <div className="flex items-center justify-center space-x-3 mb-4">
            <Icon name="CalculatorIcon" size={32} variant="solid" className="text-accent" />
            <h2 className="text-3xl md:text-4xl font-headline font-bold text-primary-foreground">
              {title}
            </h2>
          </div>
          <p className="text-lg text-primary-foreground opacity-90 max-w-3xl mx-auto">
            {description}
          </p>
        </div>
        
        <div className="bg-card rounded-lg p-8 shadow-partnership">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="space-y-6">
              <h3 className="text-xl font-headline font-semibold text-foreground mb-6">
                Your Project Details
              </h3>
              
              <div>
                <label className="block text-sm font-body font-semibold text-foreground mb-3">
                  Team Size: {inputs.teamSize} members
                </label>
                <input
                  type="range"
                  min="5"
                  max="50"
                  value={inputs.teamSize}
                  onChange={(e) => handleInputChange('teamSize', parseInt(e.target.value))}
                  className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer accent-accent"
                />
                <div className="flex justify-between text-xs text-muted-foreground mt-2">
                  <span>5</span>
                  <span>50</span>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-body font-semibold text-foreground mb-3">
                  Project Duration: {inputs.projectDuration} months
                </label>
                <input
                  type="range"
                  min="3"
                  max="24"
                  value={inputs.projectDuration}
                  onChange={(e) => handleInputChange('projectDuration', parseInt(e.target.value))}
                  className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer accent-accent"
                />
                <div className="flex justify-between text-xs text-muted-foreground mt-2">
                  <span>3</span>
                  <span>24</span>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-body font-semibold text-foreground mb-3">
                  Current Efficiency: {inputs.currentEfficiency}%
                </label>
                <input
                  type="range"
                  min="40"
                  max="90"
                  value={inputs.currentEfficiency}
                  onChange={(e) => handleInputChange('currentEfficiency', parseInt(e.target.value))}
                  className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer accent-accent"
                />
                <div className="flex justify-between text-xs text-muted-foreground mt-2">
                  <span>40%</span>
                  <span>90%</span>
                </div>
              </div>
            </div>
            
            <div className="bg-gradient-to-br from-accent to-conversion p-8 rounded-lg text-white">
              <h3 className="text-xl font-headline font-semibold mb-6">
                Projected Results with Invictosoft
              </h3>
              
              <div className="space-y-6">
                <div className="bg-white bg-opacity-10 backdrop-blur-sm rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm opacity-90">Cost Savings</span>
                    <Icon name="CurrencyDollarIcon" size={20} variant="solid" />
                  </div>
                  <div className="text-3xl font-headline font-bold">
                    ${results.costSavings.toLocaleString()}
                  </div>
                </div>
                
                <div className="bg-white bg-opacity-10 backdrop-blur-sm rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm opacity-90">Time Reduction</span>
                    <Icon name="ClockIcon" size={20} variant="solid" />
                  </div>
                  <div className="text-3xl font-headline font-bold">
                    {results.timeReduction} months
                  </div>
                </div>
                
                <div className="bg-white bg-opacity-10 backdrop-blur-sm rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm opacity-90">Productivity Gain</span>
                    <Icon name="ArrowTrendingUpIcon" size={20} variant="solid" />
                  </div>
                  <div className="text-3xl font-headline font-bold">
                    {results.productivityGain}%
                  </div>
                </div>
                
                <div className="bg-white bg-opacity-10 backdrop-blur-sm rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm opacity-90">Return on Investment</span>
                    <Icon name="ChartBarIcon" size={20} variant="solid" />
                  </div>
                  <div className="text-3xl font-headline font-bold">
                    {results.roi}%
                  </div>
                </div>
              </div>
              
              <button className="w-full mt-6 px-6 py-3 bg-white text-accent font-headline font-semibold rounded-md hover:bg-opacity-90 shadow-cta transition-all duration-base">
                Schedule ROI Discussion
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ROICalculator;